import { getUrl, downloadData, uploadData, remove } from '@aws-amplify/storage';

export default {
  methods: {
    async getFileFromS3(path, bucket = "landing") {
      const result = await getUrl({
        path,
        options: {
          bucket,
        },
      });
      try {
        // console.log(path, "this is the S3 path in getFileFromS3");

        // Try using getUrl first to get a presigned URL
        try {
          const downloadResult = await getUrl({
            path,
            options: {
              bucket,
            },
          });

          const url = downloadResult.url.href || downloadResult.url;
          // console.log("Got presigned URL from Amplify:", url);

          // Fetch the file using the presigned URL
          const response = await fetch(url);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const text = await response.text();
          // console.log("File downloaded successfully from S3 using getUrl");
          return text;
        } catch (getUrlError) {
          console.warn("getUrl failed, trying downloadData:", getUrlError);

          // Fallback to downloadData
          const { body } = await downloadData({
            path,
            options: {
              bucket,
            },
          }).result;

          const blob = await body.blob();
          const text = await blob.text();
          // console.log("File downloaded successfully from S3 using downloadData");
          return text;
        }
      } catch (error) {
        console.error("Error retrieving file from S3:", error);
        throw error;
      }
    },
    /**
     * Get presigned URL for a file in S3
     * @param {string} path - S3 path to the file
     * @returns {Promise<string|null>} - Presigned URL or null if path is empty
     */
    async getFileUrl(path, bucket = "landing") {
      if (!path) return null;

      const result = await getUrl({
        path: this.normalizeS3Path(path),
        options: {
          bucket,
        },
      });

      return result.url.href;
    },
    normalizeS3Path(path) {
      if (!path) return "";

      const trimmed = String(path).trim();

      if (trimmed.startsWith("http")) {
        try {
          const url = new URL(trimmed);
          return decodeURIComponent(url.pathname.replace(/^\/+/, ""));
        } catch {
          return trimmed;
        }
      }

      return trimmed.replace(/^\/+/, "");
    },
    async downloadFileFromS3(path, fileName = null, bucket = "landing") {
      const normalizedPath = this.normalizeS3Path(path);

      if (!normalizedPath) {
        throw new Error("Invalid file path");
      }

      const { body } = await downloadData({
        path: normalizedPath,
        options: {
          bucket,
        },
      }).result;

      const blob = await body.blob();
      const downloadName =
        fileName || normalizedPath.split("/").pop() || "download";
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    },
    /**
     * Upload a file to S3
     * @param {File} file - File to upload
     * @param {string} newPath - S3 path where file should be uploaded
     * @param {string|null} oldPath - Optional old S3 path to delete before uploading
     * @param {object} metadata - Optional metadata to store with the object
     * @returns {Promise<string>} - The saved S3 path
     */
    async uploadFile(
      file,
      newPath,
      bucket = "landing",
      oldPath = null,
      metadata = {}
    ) {
      if (oldPath) {
        try {
          await remove({
            path: oldPath,
            options: {
              bucket,
            },
          });
        } catch (removeErr) {
          console.warn("Failed to delete old file:", removeErr);
        }
      }

      const { result } = await uploadData({
        path: newPath,
        data: file,
        options: {
          bucket,
          contentType: file.type || "image/png",
          metadata,
        },
      });

      return result?.path || newPath;
    },
    /**
     * Upload a large file to S3 with progress tracking
     *
     * @param {File} file
     * @param {string} newPath
     * @param {string} bucket
     * @param {string|null} oldPath
     * @param {object} metadata
     * @param {Function|null} onProgress
     * @returns {Promise<string>}
     */
    async uploadLargeFile(
      file,
      newPath,
      bucket = "landing",
      oldPath = null,
      metadata = {},
      onProgress = null
    ) {
      try {
        console.log("=======================================");
        console.log("Starting S3 Upload");
        // console.log("Bucket:", bucket);
        // console.log("Path:", newPath);
        console.log("File:", file.name);
        console.log(
          "Size:",
          (file.size / 1024 / 1024).toFixed(2),
          "MB"
        );
        console.log("=======================================");

        // Delete existing file if needed
        if (oldPath) {
          try {
            console.log("Deleting old file:", oldPath);

            await remove({
              path: oldPath,
              options: {
                bucket,
              },
            });

            console.log("Old file deleted.");
          } catch (err) {
            console.warn("Failed deleting old file:", err);
          }
        }

        // Start upload
        const uploadTask = uploadData({
          path: newPath,
          data: file,
          options: {
            bucket,
            contentType: file.type || "application/octet-stream",
            metadata,

            onProgress: ({ transferredBytes = 0, totalBytes }) => {
              const total = totalBytes || file.size;

              const percent = Number(
                ((transferredBytes / total) * 100).toFixed(2)
              );

              // Status for Progress
              // console.log(
              //   `[S3 Upload] ${percent}% | ${(
              //     transferredBytes /
              //     1024 /
              //     1024
              //   ).toFixed(2)} MB / ${(
              //     total /
              //     1024 /
              //     1024
              //   ).toFixed(2)} MB`
              // );

              if (typeof onProgress === "function") {
                onProgress({
                  percent,
                  transferredBytes,
                  totalBytes: total,
                });
              }
            },
          },
        });

        console.log("Upload Task:", uploadTask);

        // Wait for upload to complete
        const { result } = await uploadTask.result;

        console.log("=======================================");
        console.log("S3 Upload Completed Successfully");
        // console.log("Saved Path:", result?.path || newPath);
        console.log("=======================================");

        return result?.path || newPath;
      } catch (error) {
        console.error("S3 Upload Failed:", error);
        throw error;
      }
    },
    /**
     * Delete a file from S3
     * @param {string} path - S3 path to the file
     * @returns {Promise<void>}
     */
    async deleteFile(path, bucket = "landing") {
      if (!path) return;

      try {
        await remove({
          path,
          options: {
            bucket,
          },
        });
      } catch (error) {
        console.warn("Failed to delete file from S3:", error);
        throw error;
      }
    }
  },
};

